import * as RToast from '@radix-ui/react-toast';
import { create } from 'zustand';
import { AlertCircle, CheckCircle2, Info, X } from 'lucide-react';
import { type ReactNode } from 'react';
import { cn } from '@/lib/cn';

type ToastVariant = 'success' | 'error' | 'info' | 'warning';
interface ToastItem {
  id: number;
  title: string;
  description?: string;
  variant: ToastVariant;
}

interface ToastStore {
  items: ToastItem[];
  push: (item: Omit<ToastItem, 'id'>) => void;
  dismiss: (id: number) => void;
}

let nextId = 1;
export const useToastStore = create<ToastStore>((set) => ({
  items: [],
  push: (item) =>
    set((s) => ({ items: [...s.items, { ...item, id: nextId++ }] })),
  dismiss: (id) => set((s) => ({ items: s.items.filter((t) => t.id !== id) })),
}));

/** Convenience helpers. */
export const toast = {
  success: (title: string, description?: string) =>
    useToastStore.getState().push({ variant: 'success', title, description }),
  error: (title: string, description?: string) =>
    useToastStore.getState().push({ variant: 'error', title, description }),
  info: (title: string, description?: string) =>
    useToastStore.getState().push({ variant: 'info', title, description }),
  warning: (title: string, description?: string) =>
    useToastStore.getState().push({ variant: 'warning', title, description }),
};

const accent: Record<ToastVariant, string> = {
  success: 'bg-status-success',
  error: 'bg-status-error',
  warning: 'bg-status-warning',
  info: 'bg-status-info',
};

const icons: Record<ToastVariant, ReactNode> = {
  success: <CheckCircle2 className="h-5 w-5 text-status-success" />,
  error: <AlertCircle className="h-5 w-5 text-status-error" />,
  warning: <AlertCircle className="h-5 w-5 text-status-warning-text" />,
  info: <Info className="h-5 w-5 text-status-info" />,
};

export function Toaster() {
  const items = useToastStore((s) => s.items);
  const dismiss = useToastStore((s) => s.dismiss);
  return (
    <RToast.Provider swipeDirection="right" duration={6000}>
      {items.map((t) => (
        <RToast.Root
          key={t.id}
          onOpenChange={(open) => !open && dismiss(t.id)}
          className={cn(
            'relative w-[360px] rounded-xl border border-line bg-white shadow-lift',
            'flex items-stretch overflow-hidden',
            'data-[state=open]:animate-fade-in data-[state=closed]:animate-fade-in',
          )}
        >
          <span className={cn('w-1 shrink-0', accent[t.variant])} />
          <div className="flex flex-1 items-start gap-3 p-3">
            <div className="mt-0.5">{icons[t.variant]}</div>
            <div className="flex-1 min-w-0">
              <RToast.Title className="text-body-strong text-ink">{t.title}</RToast.Title>
              {t.description && (
                <RToast.Description className="text-caption text-ink-muted mt-0.5">
                  {t.description}
                </RToast.Description>
              )}
            </div>
            <RToast.Close
              className="-mr-1 -mt-1 rounded p-1 text-ink-muted hover:bg-canvas"
              aria-label="Dismiss"
            >
              <X className="h-3.5 w-3.5" />
            </RToast.Close>
          </div>
        </RToast.Root>
      ))}
      <RToast.Viewport
        className={cn(
          'fixed top-4 right-4 z-[100] flex w-[380px] max-w-[100vw] flex-col gap-2',
          'outline-none',
        )}
      />
    </RToast.Provider>
  );
}
